import React, { useMemo, useState } from "react";
import { generateSceneFromKitchenIntent } from "../../ai/generateSceneFromKitchenIntent";
import { KITCHEN_DESIGN_INTENT_SCHEMA } from "../../ai/kitchenDesignIntent.schema";
import { createInteriorAiJob,  waitForInteriorAiJob,} from "../../services/interiorAiJobApi";
import AiKitchenQuickForm from "./AiKitchenQuickForm";
import { normalizeKitchenIntent } from "../../ai/normalizeKitchenIntent";
import { SCENE_EDIT_ACTION_SCHEMA } from "../../ai/sceneEditActions.schema";
import { validateSceneEditActions } from "../../ai/validateSceneEditActions";
import { KITCHEN_DESIGN_SESSION_SCHEMA } from "../../ai/kitchenDesignSession.schema";
import { validateKitchenIntent } from "../../ai/validateKitchenIntent";



const SAMPLE_LINEAR_INTENT = {
  version: 1,
  layoutType: "linear",
  description: "Simple modern base kitchen line",
  room: {
    wallA: 3000,
    wallB: 0,
    height: 2600,
  },
  materials: {},
  runs: [
    {
      id: "main_base",
      type: "base",
      startX: 0,
      startZ: 0,
      rotationY: 0,
      defaultHeight: 720,
      defaultDepth: 560,
      floorOffset: 0,
      cabinets: [
        { width: 600, frontType: "doors", doorCount: 2, shelfCount: 1 },
        { width: 800, frontType: "drawers", drawerCount: 3 },
        { width: 600, frontType: "doors", doorCount: 1, shelfCount: 2 },
      ],
    },
  ],
};

const SAMPLE_L_SHAPE_INTENT = {
  version: 1,
  layoutType: "l_shape",
  description: "L shaped kitchen with two cabinet runs",
  room: {
    wallA: 3000,
    wallB: 2200,
    height: 2600,
  },
  materials: {},
  runs: [
    {
      id: "main_base",
      type: "base",
      startX: 0,
      startZ: 0,
      rotationY: 0,
      defaultHeight: 720,
      defaultDepth: 560,
      floorOffset: 0,
      cabinets: [
        { width: 600, frontType: "doors", doorCount: 2, shelfCount: 1 },
        { width: 800, frontType: "drawers", drawerCount: 3 },
      ],
    },
    {
      id: "side_base",
      type: "base",
      startX: 1400,
      startZ: -560,
      rotationY: 90,
      defaultHeight: 720,
      defaultDepth: 560,
      floorOffset: 0,
      cabinets: [
        { width: 600, frontType: "doors", doorCount: 1, shelfCount: 1 },
        { width: 600, frontType: "drawers", drawerCount: 3 },
      ],
    },
  ],
};

function prettyJson(value) {
  return JSON.stringify(value, null, 2);
}

function parseJson(text) {
  try {
    return {
      ok: true,
      value: JSON.parse(text),
      error: null,
    };
  } catch (err) {
    return {
      ok: false,
      value: null,
      error: err.message,
    };
  }
}

function PreviewNodeList({ nodes = [] }) {
  if (!nodes.length) {
    return (
      <div className="border rounded p-3 bg-slate-50 text-sm text-slate-500">
        No generated nodes yet.
      </div>
    );
  }

  return (
    <div className="border rounded bg-white overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 border-b text-sm font-medium">
        Generated nodes: {nodes.length}
      </div>

      <div className="divide-y max-h-[260px] overflow-auto">
        {nodes.map((node) => (
          <div key={node.id} className="px-3 py-2 text-sm">
            <div className="font-medium text-slate-800">
              {node.name || node.id}
            </div>

            <div className="text-xs text-slate-500">
              width {node.parameterOverrides?.width ?? "—"} · height{" "}
              {node.parameterOverrides?.height ?? "—"} · depth{" "}
              {node.parameterOverrides?.depth ?? "—"}
            </div>

            <div className="text-[11px] text-slate-400">
              X {node.transform?.position?.x ?? 0} · Y{" "}
              {node.transform?.position?.y ?? 0} · Z{" "}
              {node.transform?.position?.z ?? 0} · RY{" "}
              {node.transform?.rotation?.y ?? 0}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AiDesignPanel({
  definitions = [],
  projectParameters = {},
  room,
  selectedNode,
  resolvedNode,
  simpleSelectedNode,
  simpleResolvedNode,
  onApplyGeneratedScene,
  onApplySceneEditActions,
}) {
  const [jsonText, setJsonText] = useState(prettyJson(SAMPLE_LINEAR_INTENT));
  const [error, setError] = useState("");
  const [generated, setGenerated] = useState(null);
  const [applyMode, setApplyMode] = useState("append");

  const [aiPrompt, setAiPrompt] = useState(
    "Design a modern linear kitchen 3000mm wide with base cabinets, mixed doors and drawers, height 720mm, depth 560mm."
  );
  const [aiLoading, setAiLoading] = useState(false);
  const [aiJobId, setAiJobId] = useState(null);
  const [aiJobStatus, setAiJobStatus] = useState("");

  const [editPrompt, setEditPrompt] = useState(
  "Ovaj element napravi širine 800mm i dodaj tri fioke."
);
const [editLoading, setEditLoading] = useState(false);
const [editJobId, setEditJobId] = useState(null);
const [editJobStatus, setEditJobStatus] = useState("");
const [editResult, setEditResult] = useState(null);
const [editError, setEditError] = useState("");
const [editValidation, setEditValidation] = useState(null);

const [guidedPrompt, setGuidedPrompt] = useState(
  "Napravi L kuhinju 3x2m sa frižider kolonom i gornjim elementima."
);

const [guidedLoading, setGuidedLoading] = useState(false);
const [guidedJobId, setGuidedJobId] = useState(null);
const [guidedJobStatus, setGuidedJobStatus] = useState("");
const [guidedSession, setGuidedSession] = useState(null);
const [guidedAnswer, setGuidedAnswer] = useState("");
const [guidedError, setGuidedError] = useState("");

  const parsedIntent = useMemo(() => {
    const result = parseJson(jsonText);
    return result.ok ? result.value : null;
  }, [jsonText]);

  function loadSample(sample) {
    setJsonText(prettyJson(sample));
    setGenerated(null);
    setError("");
  }

  function buildLocalSceneEditContext() {
  return {
    selected: resolvedNode
      ? {
          id: resolvedNode.id,
          name: resolvedNode.name,
          category:
            resolvedNode.category ||
            resolvedNode.definition?.category ||
            resolvedNode.nodeType ||
            "",
          isGenerated: Boolean(
            resolvedNode.parentGeneratedFromId ||
              resolvedNode.panelKey ||
              resolvedNode.overrideKey
          ),
          parentGeneratedFromId: resolvedNode.parentGeneratedFromId || null,
          partitionSide: resolvedNode.partitionSide || null,
          panelKey: resolvedNode.panelKey || null,
          parameters: resolvedNode.effectiveParameters || {},
        }
      : null,

    simpleSelected: simpleResolvedNode
      ? {
          id: simpleResolvedNode.id,
          name: simpleResolvedNode.name,
          category:
            simpleResolvedNode.category ||
            simpleResolvedNode.definition?.category ||
            simpleResolvedNode.nodeType ||
            "",
          parameters: simpleResolvedNode.effectiveParameters || {},
        }
      : null,

    projectParameters,
  };
}

async function startGuidedDesign() {
  setGuidedError("");
  setGuidedLoading(true);
  setGuidedJobId(null);
  setGuidedJobStatus("Creating guided design job...");
  setGuidedSession(null);
  setGuidedAnswer("");

  try {
    const job = await createInteriorAiJob({
      jobType: "kitchen_design_session",
      prompt: guidedPrompt,
      inputJson: {
        language: "sr",
        schema: KITCHEN_DESIGN_SESSION_SCHEMA,
        projectParameters,
      },
      contextJson: {
  room,
  projectParameters,
},
      priority: 100,
    });

    setGuidedJobId(job.id);
    setGuidedJobStatus(`Job #${job.id}: pending`);

    const completedJob = await waitForInteriorAiJob(job.id, {
      intervalMs: 2000,
      timeoutMs: 120000,
      onTick: (jobTick) => {
        setGuidedJobStatus(`Job #${jobTick.id}: ${jobTick.status}`);
      },
    });

    const session = completedJob?.result_json?.session;

    if (!session) {
      throw new Error("Guided job completed, but result_json.session is missing.");
    }

    setGuidedSession(session);
    setGuidedJobStatus(`Job #${completedJob.id}: completed`);
  } catch (err) {
    console.error("guided design error:", err);
    setGuidedError(err.message || "Guided design failed.");
    setGuidedJobStatus("Failed");
  } finally {
    setGuidedLoading(false);
  }
}

async function continueGuidedDesign() {
  if (!guidedSession) {
    setGuidedError("Start guided design first.");
    return;
  }

  if (
  guidedSession?.status === "needs_clarification" &&
  !guidedAnswer.trim()
) {
  setGuidedError("Unesi odgovor na pitanja.");
  return;
}

  setGuidedError("");
  setGuidedLoading(true);
  setGuidedJobStatus("Generating final kitchen intent...");

  try {
    const finalPrompt =
      guidedSession.nextPrompt?.trim() ||
      buildGuidedFinalPrompt({
        prompt: guidedPrompt,
        session: guidedSession,
        answer: guidedAnswer,
      });

    const job = await createInteriorAiJob({
      jobType: "kitchen_intent",
      prompt: finalPrompt,
      inputJson: {
        language: "sr",
        schema: KITCHEN_DESIGN_INTENT_SCHEMA,
        projectParameters,
      },
      contextJson: {
  room,
  projectParameters,
  guidedSession,
  guidedAnswer,
},
      priority: 100,
    });

    setGuidedJobId(job.id);
    setGuidedJobStatus(`Final job #${job.id}: pending`);

    const completedJob = await waitForInteriorAiJob(job.id, {
      intervalMs: 2000,
      timeoutMs: 120000,
      onTick: (jobTick) => {
        setGuidedJobStatus(`Final job #${jobTick.id}: ${jobTick.status}`);
      },
    });

    const intent = completedJob?.result_json?.intent;

    if (!intent) {
      throw new Error("Final AI job completed, but result_json.intent is missing.");
    }

const normalizedIntent = normalizeKitchenIntent(intent);
const validation = validateKitchenIntent(normalizedIntent);

if (!validation.ok) {
  setGuidedError(
    validation.errors
      .map((e) => e.message)
      .join("\n")
  );
  setGenerated(null);
  return;
}

setJsonText(prettyJson(normalizedIntent));

const generatedResult = generateSceneFromKitchenIntent({
  intent: normalizedIntent,
  definitions,
});

if (!generatedResult.nodes?.length) {
  throw new Error("Final intent was created, but no scene nodes were generated.");
}

setGenerated(generatedResult);
setGuidedJobStatus(`Final job #${completedJob.id}: completed`);
  } catch (err) {
    console.error("guided final generation error:", err);
    setGuidedError(err.message || "Final guided generation failed.");
    setGuidedJobStatus("Failed");
  } finally {
    setGuidedLoading(false);
  }
}

function buildGuidedFinalPrompt({ prompt, session, answer }) {
  return `
Original user request:
${prompt}

AI understanding:
${session?.summary || ""}

Known information:
${JSON.stringify(session?.known || {}, null, 2)}

Missing information that user answered:
${answer}

Assumptions:
${JSON.stringify(session?.assumptions || [], null, 2)}

Now generate the final Kitchen Intent JSON.

Use wall-based placement only:
- use wallId and offsetFromWallStart
- do not use startX, startZ, rotationY unless there is no wall available
- use back_wall as the main wall unless the user says otherwise
- base cabinets and wall cabinets on the same wall must use the same wallId and offsetFromWallStart
- base cabinets use floorOffset 150mm unless specified otherwise
- wall cabinets use floorOffset 1500mm unless specified otherwise
- cabinet backs must align to the wall
- total run width + offsetFromWallStart must not exceed wall length

Use all confirmed information.
If user answered a missing question, prefer the user answer.
Return valid JSON only.
`.trim();
}

async function generateWithLocalAi() {
  setError("");
  setAiLoading(true);
  setAiJobId(null);
  setAiJobStatus("Creating AI job...");

  try {
    const job = await createInteriorAiJob({
      jobType: "kitchen_intent",
      prompt: aiPrompt,
      inputJson: {
        language: "sr",
        schema: KITCHEN_DESIGN_INTENT_SCHEMA,
        projectParameters,
      },
      contextJson: {
  room,
  projectParameters,
},
      priority: 100,
    });

    setAiJobId(job.id);
    setAiJobStatus(`Job #${job.id}: pending`);

    const completedJob = await waitForInteriorAiJob(job.id, {
      intervalMs: 2000,
      timeoutMs: 120000,
      onTick: (jobTick) => {
        setAiJobStatus(`Job #${jobTick.id}: ${jobTick.status}`);
      },
    });

    const intent = completedJob?.result_json?.intent;

    if (!intent) {
      throw new Error("AI job completed, but result_json.intent is missing.");
    }

    const normalizedIntent = normalizeKitchenIntent(intent);
    const nextJsonText = prettyJson(normalizedIntent);

    setJsonText(nextJsonText);

    const generatedResult = generateSceneFromKitchenIntent({
      intent: normalizedIntent,
      definitions,
    });

    if (!generatedResult.nodes?.length) {
      setError(
        "AI job returned JSON, but generator did not create any nodes. Check cabinet definitions."
      );
      setGenerated(null);
      return;
    }

    setGenerated(generatedResult);
    setAiJobStatus(`Job #${completedJob.id}: completed`);
  } catch (err) {
    console.error("AI job generation error:", err);
    setError(err.message || "AI generation failed.");
    setGenerated(null);
    setAiJobStatus("Failed");
  } finally {
    setAiLoading(false);
  }
}

async function generateSceneEditActions() {
  setEditError("");
  setEditLoading(true);
  setEditJobId(null);
  setEditJobStatus("Creating scene edit job...");
  setEditResult(null);

  try {
    const job = await createInteriorAiJob({
      jobType: "scene_edit",
      prompt: editPrompt,
      inputJson: {
        language: "sr",
        schema: SCENE_EDIT_ACTION_SCHEMA,
      },
      contextJson: buildLocalSceneEditContext(),
      priority: 100,
    });

    setEditJobId(job.id);
    setEditJobStatus(`Job #${job.id}: pending`);

    const completedJob = await waitForInteriorAiJob(job.id, {
      intervalMs: 2000,
      timeoutMs: 120000,
      onTick: (jobTick) => {
        setEditJobStatus(`Job #${jobTick.id}: ${jobTick.status}`);
      },
    });

    const result = completedJob?.result_json?.editResult;

    if (!result) {
      throw new Error("AI job completed, but result_json.editResult is missing.");
    }

    const validation = validateSceneEditActions({
  editResult: result,
  selectedNode,
  resolvedNode,
  simpleSelectedNode,
  simpleResolvedNode,
});

setEditValidation(validation);
setEditResult(result);
setEditJobStatus(`Job #${completedJob.id}: completed`);
  } catch (err) {
    console.error("scene edit AI error:", err);
    setEditError(err.message || "Scene edit AI failed.");
    setEditJobStatus("Failed");
  } finally {
    setEditLoading(false);
  }
}

function applySceneEditActions() {
  if (!editResult?.actions?.length) {
    setEditError("No scene edit actions to apply.");
    return;
  }

  const validation = validateSceneEditActions({
    editResult,
    selectedNode,
    resolvedNode,
    simpleSelectedNode,
    simpleResolvedNode,
  });

  setEditValidation(validation);

  if (!validation.ok) {
    setEditError("AI actions are not valid. Review validation messages.");
    return;
  }

  onApplySceneEditActions?.(editResult);
}

  function generatePreview() {
  setError("");

  const parsed = parseJson(jsonText);

  if (!parsed.ok) {
    setError(`Invalid JSON: ${parsed.error}`);
    setGenerated(null);
    return;
  }

  try {
    const normalizedIntent = normalizeKitchenIntent(parsed.value);
    const validation = validateKitchenIntent(normalizedIntent);

    if (!validation.ok) {
      setError(
        validation.errors
          .map((e) => e.message)
          .join("\n")
      );
      setGenerated(null);
      return;
    }

    setJsonText(prettyJson(normalizedIntent));

    const result = generateSceneFromKitchenIntent({
      intent: normalizedIntent,
      definitions,
    });

    if (!result.nodes?.length) {
      setError(
        "Generator did not create any nodes. Check that cabinet definitions exist."
      );
    }

    setGenerated(result);
  } catch (err) {
    console.error("AI intent generation error:", err);
    setError(err?.message || "Failed to generate scene from intent.");
    setGenerated(null);
  }
}

  function applyToScene() {
    if (!generated?.nodes?.length) {
      setError("Generate preview first.");
      return;
    }

    onApplyGeneratedScene?.({
      mode: applyMode,
      nodes: generated.nodes,
      projectParametersPatch: generated.projectParametersPatch || {},
      intent: parsedIntent,
    });
  }

  return (
    <div className="space-y-4">
      {/* <AiKitchenQuickForm
        projectParameters={projectParameters}
        onCreateIntent={(intent) => {
          setJsonText(prettyJson(intent));
          setGenerated(null);
          setError("");
        }}
      /> */}

      <div className="border rounded p-3 bg-white space-y-3">
  <div>
    <div className="font-semibold text-sm text-slate-800">
      Guided AI Design
    </div>
    <div className="text-xs text-slate-500">
      Napiši jednostavan zahtev. AI će tražiti dopunu ako nema dovoljno informacija.
    </div>
  </div>

  <textarea
    value={guidedPrompt}
    onChange={(e) => {
      setGuidedPrompt(e.target.value);
      setGuidedSession(null);
      setGuidedError("");
      setGuidedAnswer("");
    }}
    className="w-full h-24 px-3 py-2 border rounded text-sm"
    placeholder="Primer: Napravi L kuhinju 3x2m sa frižider kolonom i gornjim elementima."
  />

  <button
    type="button"
    onClick={startGuidedDesign}
    disabled={guidedLoading || !guidedPrompt.trim()}
    className="w-full px-3 py-2 rounded bg-blue-600 text-white text-sm disabled:opacity-40"
  >
    {guidedLoading ? "Thinking..." : "Start guided design"}
  </button>

  {guidedJobId && (
    <div className="border rounded p-2 bg-slate-50 text-xs text-slate-600">
      <div>
        <strong>Guided Job:</strong> #{guidedJobId}
      </div>
      <div>
        <strong>Status:</strong> {guidedJobStatus || "pending"}
      </div>
    </div>
  )}

  {guidedError && (
    <div className="border rounded p-3 bg-red-50 text-sm text-red-700">
      {guidedError}
    </div>
  )}

  {guidedSession && (
    <div className="border rounded bg-white overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 border-b text-sm font-medium">
        AI understanding
      </div>

      <div className="p-3 space-y-3 text-sm">
        <div>
          <div className="text-xs font-semibold text-slate-500">Summary</div>
          <div className="text-slate-700">{guidedSession.summary}</div>
        </div>

        {!!guidedSession.assumptions?.length && (
          <div>
            <div className="text-xs font-semibold text-slate-500">
              Assumptions
            </div>
            <ul className="list-disc ml-5 text-slate-700">
              {guidedSession.assumptions.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {guidedSession.status === "needs_clarification" && (
          <div className="border rounded p-3 bg-amber-50 space-y-3">
            <div className="font-semibold text-sm text-amber-800">
              AI needs clarification
            </div>

            {(guidedSession.questions || []).map((q, index) => (
              <div key={q.key || index} className="space-y-1">
                <div className="text-sm text-amber-900">
                  {index + 1}. {q.question}
                </div>

                {!!q.options?.length && (
                  <div className="flex flex-wrap gap-1">
                    {q.options.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          setGuidedAnswer((prev) =>
                            `${prev ? `${prev}\n` : ""}${q.question}: ${option}`
                          );
                        }}
                        className="px-2 py-1 rounded border bg-white text-xs text-slate-700"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <textarea
              value={guidedAnswer}
              onChange={(e) => setGuidedAnswer(e.target.value)}
              className="w-full h-24 px-3 py-2 border rounded text-sm bg-white"
              placeholder="Odgovori na pitanja ovde..."
            />

            <button
              type="button"
              onClick={continueGuidedDesign}
              disabled={guidedLoading || !guidedAnswer.trim()}
              className="w-full px-3 py-2 rounded bg-green-600 text-white text-sm disabled:opacity-40"
            >
              Continue and generate design
            </button>
          </div>
        )}

        {guidedSession.status === "ready_to_generate" && (
          <button
            type="button"
            onClick={continueGuidedDesign}
            disabled={guidedLoading}
            className="w-full px-3 py-2 rounded bg-green-600 text-white text-sm disabled:opacity-40"
          >
            Generate design from guided session
          </button>
        )}
      </div>
    </div>
  )}
</div>

      <div className="border rounded p-3 bg-white space-y-3">
        <div>
          <div className="font-semibold text-sm text-slate-800">
            Local AI prompt
          </div>

          <div className="text-xs text-slate-500">
            Prompt se šalje lokalnom modelu i vraća Kitchen Intent JSON.
          </div>
        </div>

        <textarea
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          className="w-full h-28 px-3 py-2 border rounded text-sm"
          placeholder="Describe kitchen..."
        />

        <button
          type="button"
          onClick={generateWithLocalAi}
          disabled={aiLoading}
          className="w-full px-3 py-2 rounded bg-purple-600 text-white text-sm disabled:opacity-40"
        >
          {aiLoading ? "Generating..." : "Generate JSON with AI job"}
        </button>

        {aiJobId && (
  <div className="border rounded p-2 bg-slate-50 text-xs text-slate-600">
    <div>
      <strong>AI Job:</strong> #{aiJobId}
    </div>
    <div>
      <strong>Status:</strong> {aiJobStatus || "pending"}
    </div>
  </div>
)}
      </div>

      <div className="border rounded p-3 bg-white space-y-3">
  <div>
    <div className="font-semibold text-sm text-slate-800">
      Edit existing scene
    </div>

    <div className="text-xs text-slate-500">
      Select an element, describe the change, then review and apply AI actions.
    </div>
  </div>

  <div className="border rounded p-2 bg-slate-50 text-xs text-slate-600">
    <div>
      <strong>Selected:</strong>{" "}
      {resolvedNode?.name || selectedNode?.name || "No selection"}
    </div>
    <div>
      <strong>Design target:</strong>{" "}
      {simpleResolvedNode?.name || simpleSelectedNode?.name || "No design target"}
    </div>
  </div>

  <textarea
    value={editPrompt}
    onChange={(e) => {
      setEditPrompt(e.target.value);
      setEditResult(null);
      setEditError("");
    }}
    className="w-full h-24 px-3 py-2 border rounded text-sm"
    placeholder="Example: Ovaj element napravi širine 800mm i dodaj tri fioke."
  />

  <button
    type="button"
    onClick={generateSceneEditActions}
    disabled={editLoading || !editPrompt.trim()}
    className="w-full px-3 py-2 rounded bg-indigo-600 text-white text-sm disabled:opacity-40"
  >
    {editLoading ? "Preparing actions..." : "Ask AI for scene edit"}
  </button>

  {editJobId && (
    <div className="border rounded p-2 bg-slate-50 text-xs text-slate-600">
      <div>
        <strong>Scene Edit Job:</strong> #{editJobId}
      </div>
      <div>
        <strong>Status:</strong> {editJobStatus || "pending"}
      </div>
    </div>
  )}

  {editError && (
    <div className="border rounded p-3 bg-red-50 text-sm text-red-700">
      {editError}
    </div>
  )}

  {editResult && (
    <div className="border rounded bg-white overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 border-b text-sm font-medium">
        Proposed actions: {editResult.actions?.length || 0}
      </div>

      {editResult.explanation && (
        <div className="px-3 py-2 text-xs text-slate-600 border-b">
          {editResult.explanation}
        </div>
      )}

      <div className="divide-y max-h-[220px] overflow-auto">
        {(editResult.actions || []).map((action, index) => (
          <div key={index} className="px-3 py-2 text-xs">
            <div className="font-semibold text-slate-800">
              {index + 1}. {action.type}
            </div>
            <pre className="mt-1 p-2 rounded bg-slate-950 text-slate-100 overflow-auto">
              {JSON.stringify(action, null, 2)}
            </pre>
          </div>
        ))}
      </div>

      {editValidation && (
  <div
    className={[
      "border rounded p-3 text-xs space-y-2",
      editValidation.ok
        ? "bg-green-50 text-green-700 border-green-200"
        : "bg-amber-50 text-amber-800 border-amber-200",
    ].join(" ")}
  >
    <div className="font-semibold">
      {editValidation.ok ? "Validation passed" : "Validation issues"}
    </div>

    {!!editValidation.errors?.length && (
      <div>
        <div className="font-medium">Errors:</div>
        <ul className="list-disc ml-4">
          {editValidation.errors.map((err, index) => (
            <li key={index}>
              {err.index !== undefined ? `Action ${err.index + 1}: ` : ""}
              {err.message}
            </li>
          ))}
        </ul>
      </div>
    )}

    {!!editValidation.warnings?.length && (
      <div>
        <div className="font-medium">Warnings:</div>
        <ul className="list-disc ml-4">
          {editValidation.warnings.map((warning, index) => (
            <li key={index}>
              {warning.index !== undefined ? `Action ${warning.index + 1}: ` : ""}
              {warning.message}
            </li>
          ))}
        </ul>
      </div>
    )}

    {editValidation.needsClarification && (
      <div>
        <div className="font-medium">AI needs clarification:</div>
        <ul className="list-disc ml-4">
          {(editValidation.questions || []).map((q, index) => (
            <li key={index}>{q.question}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
)}

      <div className="p-3">
        <button
  type="button"
  onClick={applySceneEditActions}
  disabled={!editResult.actions?.length || editValidation?.ok === false}
  className="w-full px-3 py-2 rounded bg-green-600 text-white text-sm disabled:opacity-40"
>
  Apply proposed changes
</button>

{editValidation?.ok === false && (
  <div className="text-xs text-amber-700 text-center">
    Fix validation issues before applying changes.
  </div>
)}
      </div>
    </div>
  )}
</div>

      <div className="border rounded p-3 bg-white space-y-3">
        <div>
          <div className="font-semibold text-sm text-slate-800">
            AI Design Intent
          </div>
          <div className="text-xs text-slate-500">
            Structured kitchen JSON used by the deterministic scene generator.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => loadSample(SAMPLE_LINEAR_INTENT)}
            className="px-3 py-2 border rounded bg-slate-50 text-sm"
          >
            Load linear sample
          </button>

          <button
            type="button"
            onClick={() => loadSample(SAMPLE_L_SHAPE_INTENT)}
            className="px-3 py-2 border rounded bg-slate-50 text-sm"
          >
            Load L-shape sample
          </button>
        </div>

        <textarea
          value={jsonText}
          onChange={(e) => {
            setJsonText(e.target.value);
            setGenerated(null);
            setError("");
          }}
          spellCheck={false}
          className="w-full h-[320px] px-3 py-2 border rounded font-mono text-xs bg-slate-950 text-slate-100"
        />

        {error && (
          <div className="border rounded p-3 bg-red-50 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={generatePreview}
            className="px-3 py-2 rounded bg-blue-600 text-white text-sm"
          >
            Generate preview
          </button>

          <select
            value={applyMode}
            onChange={(e) => setApplyMode(e.target.value)}
            className="px-3 py-2 border rounded bg-white text-sm"
          >
            <option value="append">Apply: append to scene</option>
            <option value="replace">Apply: replace scene</option>
          </select>
        </div>
      </div>

      <PreviewNodeList nodes={generated?.nodes || []} />

      <button
        type="button"
        onClick={applyToScene}
        disabled={!generated?.nodes?.length}
        className="w-full px-3 py-2 rounded bg-green-600 text-white text-sm disabled:opacity-40"
      >
        Apply generated scene
      </button>
    </div>
  );
}